/**
 * FeedbackPage
 *
 * Admin page for viewing and managing user feedback.
 * Features summary stats, filter tabs, searchable list, and expandable row details.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  Bug,
  Lightbulb,
  Heart,
  FileText,
  Search,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import {
  useGetFeedback,
  useUpdateFeedback,
  FeedbackItem,
  FeedbackType,
  FeedbackStatus,
  FeedbackPriority,
  FeedbackCounts,
  formatRelativeTime,
} from '../hooks/useAdminApi';
import { StatusBadge, PriorityBadge } from '../components';

type FilterTab = 'all' | 'new' | 'bugs' | 'features' | 'praise' | 'resolved';

function getTypeIcon(type: FeedbackType): JSX.Element {
  switch (type) {
    case 'bug':
      return <Bug className="w-4 h-4 text-red-500" />;
    case 'feature':
      return <Lightbulb className="w-4 h-4 text-yellow-500" />;
    case 'praise':
      return <Heart className="w-4 h-4 text-pink-500" />;
    default:
      return <FileText className="w-4 h-4 text-gray-500" />;
  }
}

function getTypeLabel(type: FeedbackType): string {
  switch (type) {
    case 'bug':
      return 'Bug';
    case 'feature':
      return 'Feature';
    case 'praise':
      return 'Praise';
    default:
      return 'General';
  }
}

export function FeedbackPage(): JSX.Element {
  const navigate = useNavigate();
  const { execute: fetchFeedback, loading, error } = useGetFeedback();
  const { mutate: updateFeedback, loading: updating } = useUpdateFeedback();

  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [counts, setCounts] = useState<FeedbackCounts>({
    total: 0,
    new: 0,
    bug: 0,
    feature: 0,
    praise: 0,
    resolved: 0,
  });
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<FeedbackItem | null>(null);

  const loadFeedback = useCallback(async () => {
    try {
      const request: { type?: FeedbackType; status?: FeedbackStatus } = {};

      // Map tab to API filter
      switch (activeTab) {
        case 'new':
          request.status = 'new';
          break;
        case 'bugs':
          request.type = 'bug';
          break;
        case 'features':
          request.type = 'feature';
          break;
        case 'praise':
          request.type = 'praise';
          break;
        case 'resolved':
          request.status = 'resolved';
          break;
      }

      const result = await fetchFeedback(request);
      setFeedback(result.feedback);
      setCounts(result.counts);
    } catch (err) {
      console.error('Failed to load feedback:', err);
    }
  }, [fetchFeedback, activeTab]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  const handleTabChange = (tab: FilterTab) => {
    setActiveTab(tab);
    setExpandedId(null);
  };

  const handleQuickAction = async (
    feedbackId: string,
    updates: { status?: FeedbackStatus; priority?: FeedbackPriority }
  ) => {
    try {
      await updateFeedback({ feedbackId, ...updates });
      await loadFeedback();
    } catch (err) {
      console.error('Failed to update feedback:', err);
    }
  };

  const handleSaveChanges = async () => {
    if (!editingItem) return;

    try {
      await updateFeedback({
        feedbackId: editingItem.feedbackId,
        status: editingItem.status,
        priority: editingItem.priority,
        internalNotes: editingItem.internalNotes,
        adminResponse: editingItem.adminResponse,
      });
      setEditingItem(null);
      await loadFeedback();
    } catch (err) {
      console.error('Failed to save changes:', err);
    }
  };

  const toggleExpanded = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setEditingItem(null);
    } else {
      setExpandedId(id);
      const item = feedback.find((f) => f.feedbackId === id);
      if (item) {
        setEditingItem({ ...item });
      }
    }
  };

  // Filter by search query
  const filteredFeedback = feedback.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.message.toLowerCase().includes(query) ||
      item.userEmail?.toLowerCase().includes(query) ||
      item.userId.toLowerCase().includes(query)
    );
  });

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: counts.total },
    { key: 'new', label: 'Unread', count: counts.new },
    { key: 'bugs', label: 'Bugs', count: counts.bug },
    { key: 'features', label: 'Features', count: counts.feature },
    { key: 'praise', label: 'Praise', count: counts.praise },
    { key: 'resolved', label: 'Resolved', count: counts.resolved },
  ];

  return (
    <div className="min-h-screen bg-airy">
      {/* Header */}
      <header className="bg-white border-b border-soft-neutral sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="btn-ghost btn-sm flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Admin
              </button>
              <div className="h-6 w-px bg-soft-neutral" />
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-evergreen" />
                <h1 className="text-xl font-semibold text-ink">Feedback</h1>
              </div>
            </div>
            <button
              onClick={loadFeedback}
              disabled={loading}
              className="btn-ghost btn-sm flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="card p-4">
            <p className="text-sm text-muted">Total</p>
            <p className="text-2xl font-semibold text-ink">{counts.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-muted">Unread</p>
            <p className="text-2xl font-semibold text-blue-600">{counts.new}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-muted">Bugs</p>
            <p className="text-2xl font-semibold text-red-600">{counts.bug}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-muted">Features</p>
            <p className="text-2xl font-semibold text-yellow-600">{counts.feature}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-muted">Praise</p>
            <p className="text-2xl font-semibold text-pink-600">{counts.praise}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-muted">Resolved</p>
            <p className="text-2xl font-semibold text-green-600">{counts.resolved}</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.key
                  ? 'bg-evergreen text-white'
                  : 'bg-soft-neutral text-ink hover:bg-slate-200'
              }`}
            >
              {tab.label}
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${
                  activeTab === tab.key ? 'bg-white/20' : 'bg-slate-300'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="text"
            placeholder="Search by message or user email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full md:w-96"
          />
        </div>

        {/* Error State */}
        {error && (
          <div className="alert alert-error flex items-center gap-2 mb-6">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && feedback.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted" />
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredFeedback.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 mx-auto text-muted mb-4" />
            <p className="text-muted">No feedback found</p>
          </div>
        )}

        {/* Feedback List */}
        <div className="space-y-2">
          {filteredFeedback.map((item) => (
            <div key={item.feedbackId} className="card overflow-hidden">
              {/* Row Header (clickable) */}
              <button
                onClick={() => toggleExpanded(item.feedbackId)}
                className="w-full px-4 py-3 flex items-center gap-4 text-left hover:bg-soft-neutral/50 transition-colors"
              >
                {/* Type Icon */}
                <div className="flex-shrink-0" title={getTypeLabel(item.type)}>
                  {getTypeIcon(item.type)}
                </div>

                {/* User Email */}
                <div className="w-40 flex-shrink-0 truncate text-sm text-muted">
                  {item.userEmail || item.userId.slice(0, 12) + '...'}
                </div>

                {/* Message Preview */}
                <div className="flex-1 truncate text-sm text-ink">{item.message}</div>

                {/* Priority Badge */}
                <div className="w-20 flex-shrink-0">
                  {item.priority && <PriorityBadge priority={item.priority} />}
                </div>

                {/* Status Badge */}
                <div className="w-24 flex-shrink-0">
                  <StatusBadge status={item.status} />
                </div>

                {/* Date */}
                <div className="w-20 flex-shrink-0 text-xs text-muted text-right">
                  {formatRelativeTime(item.createdAt)}
                </div>

                {/* Expand Icon */}
                <div className="flex-shrink-0">
                  {expandedId === item.feedbackId ? (
                    <ChevronUp className="w-4 h-4 text-muted" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted" />
                  )}
                </div>
              </button>

              {/* Quick Actions (visible without expanding) */}
              {expandedId !== item.feedbackId && item.status === 'new' && (
                <div className="px-4 pb-3 pt-0 flex gap-2 border-t border-soft-neutral">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAction(item.feedbackId, { status: 'reviewing' });
                    }}
                    disabled={updating}
                    className="btn-ghost btn-sm text-xs"
                  >
                    Mark Reviewing
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAction(item.feedbackId, { status: 'resolved' });
                    }}
                    disabled={updating}
                    className="btn-ghost btn-sm text-xs"
                  >
                    Resolve
                  </button>
                  <select
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleQuickAction(item.feedbackId, {
                          priority: e.target.value as FeedbackPriority,
                        });
                      }
                    }}
                    className="text-xs border border-soft-neutral rounded px-2 py-1 bg-white"
                    defaultValue=""
                  >
                    <option value="">Set Priority...</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              )}

              {/* Expanded Detail */}
              {expandedId === item.feedbackId && editingItem && (
                <div className="px-4 pb-4 pt-2 border-t border-soft-neutral space-y-4">
                  {/* Full Message */}
                  <div>
                    <label className="label">Full Message</label>
                    <p className="text-sm text-ink bg-soft-neutral/50 p-3 rounded-lg whitespace-pre-wrap">
                      {item.message}
                    </p>
                  </div>

                  {/* Context Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {item.page && (
                      <div>
                        <label className="label">Page</label>
                        <p className="text-sm text-muted">{item.page}</p>
                      </div>
                    )}
                    {item.userAgent && (
                      <div>
                        <label className="label">User Agent</label>
                        <p className="text-sm text-muted truncate" title={item.userAgent}>
                          {item.userAgent}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Editable Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Priority</label>
                      <select
                        value={editingItem.priority || ''}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            priority: (e.target.value as FeedbackPriority) || undefined,
                          })
                        }
                        className="input"
                      >
                        <option value="">No Priority</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Status</label>
                      <select
                        value={editingItem.status}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            status: e.target.value as FeedbackStatus,
                          })
                        }
                        className="input"
                      >
                        <option value="new">New</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="resolved">Resolved</option>
                        <option value="wont_fix">Won't Fix</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="label">Internal Notes (admin only)</label>
                    <textarea
                      value={editingItem.internalNotes || ''}
                      onChange={(e) =>
                        setEditingItem({ ...editingItem, internalNotes: e.target.value })
                      }
                      placeholder="Add internal notes..."
                      className="input min-h-[80px]"
                    />
                  </div>

                  <div>
                    <label className="label">Admin Response (visible to user)</label>
                    <textarea
                      value={editingItem.adminResponse || ''}
                      onChange={(e) =>
                        setEditingItem({ ...editingItem, adminResponse: e.target.value })
                      }
                      placeholder="Write a response to the user..."
                      className="input min-h-[80px]"
                    />
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setExpandedId(null);
                        setEditingItem(null);
                      }}
                      className="btn-ghost"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveChanges}
                      disabled={updating}
                      className="btn-primary flex items-center gap-2"
                    >
                      {updating && <Loader2 className="w-4 h-4 animate-spin" />}
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default FeedbackPage;
